import { differenceInCalendarDays, formatDistanceToNowStrict } from "date-fns";

import { formatBytes, formatDuration } from "../../lib/t";
import type { IParticipant } from "../../shared/db/participant";
import type {
  ProjectDataDownloadFlowMetadata,
  UserDownload,
  UserDownloadInfo,
} from "../../model/download";
import type { ProgressBarData } from "../../lib/data_download/progress";

export const ACTIVE_PACKAGE_ID = "active-download-package";

export type PackageStatus = "zipping" | "ready" | "expired";

export interface ParticipantDisplay {
  id: string;
  label: string;
}

export interface DownloadPackageFileViewModel {
  name: string;
  participantCount: number;
  sizeText: string;
  url: string;
}

export interface DownloadPackageViewModel {
  id: string;
  status: PackageStatus;
  title: string;
  subtitle: string;
  participantIds: string[];
  participantsText: string;
  participantSubText: string;
  dateRangeText: string;
  durationText: string;
  sizeText: string;
  fileCountText: string;
  createdDateText: string;
  createdMetaText: string;
  expiryText: string;
  sidebarMetaText: string;
  progressBarData?: ProgressBarData;
  files: DownloadPackageFileViewModel[];
}

const metadataToDownloadInfo = (
  metadata: Pick<ProjectDataDownloadFlowMetadata, "input">,
): UserDownloadInfo => {
  const { input } = metadata;
  return {
    projectId: input.projectId,
    participantIds: [...input.participantIds].sort(),
    startDate: input.startDateIso ? new Date(input.startDateIso) : undefined,
    endDate: input.endDateIso ? new Date(input.endDateIso) : undefined,
  };
};

const formatShortDate = (value: Date | string | number, timeZone?: string): string => {
  return new Date(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone,
  });
};

const formatShortDateNoYear = (value: Date | string | number): string => {
  return new Date(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

const formatTime = (value: Date | string | number): string => {
  return new Date(value).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const formatDateRange = (startDate?: Date, endDate?: Date): string => {
  if (startDate && endDate) {
    return `${formatShortDate(startDate, "UTC")} - ${formatShortDate(endDate, "UTC")}`;
  }
  if (startDate) {
    return `${formatShortDate(startDate, "UTC")} - Now`;
  }
  if (endDate) {
    return `Through ${formatShortDate(endDate, "UTC")}`;
  }
  return "All time";
};

const formatDurationText = (startDate?: Date, endDate?: Date): string => {
  if (!startDate || !endDate) return "All available data";
  const days = Math.max(differenceInCalendarDays(endDate, startDate) + 1, 1);
  return `${days} day${days === 1 ? "" : "s"}`;
};

const formatExpiryText = (metadata: ProjectDataDownloadFlowMetadata): string => {
  if (!metadata.expireAt)
    return metadata.isExpired ? "Expired" : "Files are kept for 30 days after creation";
  const expireAt = new Date(metadata.expireAt);
  if (metadata.isExpired) {
    return `Expired ${formatShortDate(expireAt)}`;
  }
  return `Expires ${formatShortDate(expireAt)} · ${formatDistanceToNowStrict(expireAt)} from now`;
};

const formatSidebarExpiryText = (metadata: ProjectDataDownloadFlowMetadata): string => {
  if (!metadata.expireAt) return metadata.isExpired ? "Expired" : "";
  const expireAt = new Date(metadata.expireAt);
  return metadata.isExpired
    ? formatShortDate(expireAt)
    : `Expires ${formatShortDateNoYear(expireAt)}`;
};

const getFilenameFromKey = (key?: string): string | null => {
  if (!key) return null;
  const filename = key.split(/[\\/]/).filter(Boolean).pop() ?? key;
  try {
    return decodeURIComponent(filename);
  } catch {
    return filename;
  }
};

const getPartInfo = (
  partsBreakdown: NonNullable<ProjectDataDownloadFlowMetadata["partsBreakdown"]>,
  index: number,
) => {
  const usesZeroBasedPartIndex = partsBreakdown.some(({ partIndex }) => partIndex === 0);
  const partIndex = usesZeroBasedPartIndex ? index : index + 1;
  return partsBreakdown.find((part) => part.partIndex === partIndex) ?? partsBreakdown[index];
};

const createArchiveFilename = (
  metadata: ProjectDataDownloadFlowMetadata,
  index: number,
  fileCount: number,
): string => {
  const baseName = `${metadata.inputHash}.zip`;
  if (fileCount === 1) return baseName;
  return baseName.replace(/^(.*?)(\.[^.]+)$/, `$1_part${index + 1}$2`);
};

export const getParticipantDisplayList = (
  participantIds: string[],
  participantById: Map<string, IParticipant>,
): ParticipantDisplay[] => {
  return participantIds.map((id) => ({
    id,
    label: participantById.get(id)?.insignia ?? id,
  }));
};

const createFileRows = (
  metadata: ProjectDataDownloadFlowMetadata,
): DownloadPackageFileViewModel[] => {
  if (!metadata.url) return [];
  const urls = Array.isArray(metadata.url) ? metadata.url : [metadata.url];
  const partsBreakdown = metadata.partsBreakdown ?? [];
  return urls.map((url, index) => {
    const part = getPartInfo(partsBreakdown, index);
    const size = part?.size ?? (urls.length === 1 ? metadata.compressedSizeInBytes : undefined);
    return {
      name: getFilenameFromKey(part?.key) ?? createArchiveFilename(metadata, index, urls.length),
      participantCount: part?.participants?.length ?? metadata.input.participantIds.length,
      sizeText: size ? formatBytes(size) : "-",
      url,
    };
  });
};

export const createMetadataPackage = (
  metadata: ProjectDataDownloadFlowMetadata,
  participantById: Map<string, IParticipant>,
): DownloadPackageViewModel | null => {
  if (metadata.failure || !metadata.success) return null;

  const status: PackageStatus | null = metadata.isExpired
    ? "expired"
    : metadata.url
      ? "ready"
      : null;
  if (!status) return null;

  const info = metadataToDownloadInfo(metadata);
  const participantDisplays = getParticipantDisplayList(info.participantIds, participantById);
  const createdAt = new Date(metadata.createdAt);
  const fileRows = status === "ready" ? createFileRows(metadata) : [];
  const requesterSuffix = metadata.requester?.email ? ` by ${metadata.requester.email}` : "";
  const sizeText = metadata.compressedSizeInBytes
    ? formatBytes(metadata.compressedSizeInBytes)
    : "-";
  const fileCountText =
    status === "ready"
      ? `${fileRows.length || 1} file${(fileRows.length || 1) === 1 ? "" : "s"}`
      : metadata.isExpired
        ? "Expired"
        : "-";

  return {
    id: metadata.id,
    status,
    title: status === "expired" ? "Expired package" : "Download File",
    subtitle:
      status === "expired"
        ? "This package expired. Files were available for 30 days after creation."
        : "Ready to download. Files are kept for 30 days after creation.",
    participantIds: info.participantIds,
    participantsText: `${participantDisplays.length}`,
    participantSubText:
      participantDisplays.length === participantById.size ? "All participants" : "Selected",
    dateRangeText: formatDateRange(info.startDate, info.endDate),
    durationText: formatDurationText(info.startDate, info.endDate),
    sizeText,
    fileCountText,
    createdDateText: formatShortDate(createdAt),
    createdMetaText: `${formatTime(createdAt)}${requesterSuffix}`,
    expiryText: formatExpiryText(metadata),
    sidebarMetaText: formatSidebarExpiryText(metadata),
    files: fileRows,
  };
};

export const createActivePackage = (
  userDownload: UserDownload | null,
  progressBarData: ProgressBarData | null,
  participantById: Map<string, IParticipant>,
): DownloadPackageViewModel | null => {
  if (!userDownload || userDownload.result) return null;

  const { info, startedAt } = userDownload;
  const participantDisplays = getParticipantDisplayList(info.participantIds, participantById);
  const bytesTotal = progressBarData?.bytesTotal;
  const startedDate = startedAt ? new Date(startedAt) : new Date();
  const remaining = progressBarData
    ? formatDuration(progressBarData.estimatedTimeRemainingInMs)
    : undefined;

  return {
    id: ACTIVE_PACKAGE_ID,
    status: "zipping",
    title: "In-progress package",
    subtitle: "Currently zipping. You'll get an email when it's ready.",
    participantIds: info.participantIds,
    participantsText: `${participantDisplays.length}`,
    participantSubText:
      participantDisplays.length === participantById.size ? "All participants" : "Selected",
    dateRangeText: formatDateRange(info.startDate, info.endDate),
    durationText: formatDurationText(info.startDate, info.endDate),
    sizeText: bytesTotal ? `~${formatBytes(bytesTotal)}` : "-",
    fileCountText: "building...",
    createdDateText: formatShortDate(startedDate),
    createdMetaText: formatTime(startedDate),
    expiryText: "Available for 30 days once ready",
    sidebarMetaText: remaining ? `~${remaining} left` : "",
    progressBarData: progressBarData ?? undefined,
    files: [],
  };
};
